import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ITiragemProdutoConfig, TIRAGEM_MINIMA_PADRAO } from '../../Config/hcp-print-run-config'

interface ITiragemConfigDialogProps {
  open: boolean
  onClose: () => void
  produtos: string[]
  config: Record<string, ITiragemProdutoConfig>
  onSave: (config: Record<string, ITiragemProdutoConfig>) => void
}

type DraftRow = {
  minimoCartela: string
  meiaCartela: string
}

export function TiragemConfigDialog({
  open,
  onClose,
  produtos,
  config,
  onSave,
}: ITiragemConfigDialogProps) {
  const [draft, setDraft] = useState<Record<string, DraftRow>>({})

  useEffect(() => {
    if (!open) return
    const initial: Record<string, DraftRow> = {}
    produtos.forEach((p) => {
      const cfg = config[p] ?? { minimoCartela: TIRAGEM_MINIMA_PADRAO }
      initial[p] = {
        minimoCartela: String(cfg.minimoCartela),
        meiaCartela: cfg.meiaCartela ? String(cfg.meiaCartela) : '',
      }
    })
    setDraft(initial)
  }, [open, produtos, config])

  const updateField = (produto: string, field: keyof DraftRow, value: string) => {
    setDraft((prev) => ({ ...prev, [produto]: { ...prev[produto], [field]: value } }))
  }

  const handleSave = () => {
    const result: Record<string, ITiragemProdutoConfig> = {}
    produtos.forEach((p) => {
      const row = draft[p]
      if (!row) return
      const minimo = parseInt(row.minimoCartela, 10)
      const meia = row.meiaCartela.trim() ? parseInt(row.meiaCartela, 10) : undefined
      if (isNaN(minimo) || minimo <= 0) return
      result[p] = {
        minimoCartela: minimo,
        ...(meia && !isNaN(meia) && meia > 0 ? { meiaCartela: meia } : {}),
      }
    })
    onSave(result)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Configuração de Tiragens
        <Typography variant="body2" color="text.secondary" fontWeight={400} mt={0.5}>
          Defina o mínimo para cartela completa e, se aplicável, o tamanho da meia cartela por produto.
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}>
              <TableCell sx={{ fontWeight: 700, pl: 3 }}>Produto</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 180 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  Mínimo Cartela
                  <Tooltip title="Tiragem acumulada necessária para fechar uma cartela completa." placement="top">
                    <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', cursor: 'help' }}>ⓘ</Box>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  Meia Cartela (múltiplo)
                  <Tooltip title="Tamanho de uma meia cartela. Ex: 500 significa que 500, 1000, 1500 un já podem fechar uma meia cartela. Deixe em branco se não se aplica." placement="top">
                    <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', cursor: 'help' }}>ⓘ</Box>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {produtos.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum produto carregado ainda. Aguarde a tela carregar os dados.
                </TableCell>
              </TableRow>
            )}
            {produtos.map((produto) => (
              <TableRow key={produto} hover>
                <TableCell sx={{ pl: 3 }}>
                  <Typography fontSize={13}>{produto}</Typography>
                </TableCell>

                <TableCell align="center">
                  <TextField
                    size="small"
                    value={draft[produto]?.minimoCartela ?? ''}
                    onChange={(e) => updateField(produto, 'minimoCartela', e.target.value)}
                    inputProps={{ style: { textAlign: 'center' }, min: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.disabled">un</Typography></InputAdornment>,
                    }}
                    sx={{ width: 130 }}
                    type="number"
                  />
                </TableCell>

                <TableCell align="center">
                  <TextField
                    size="small"
                    value={draft[produto]?.meiaCartela ?? ''}
                    onChange={(e) => updateField(produto, 'meiaCartela', e.target.value)}
                    placeholder="—"
                    inputProps={{ style: { textAlign: 'center' }, min: 1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.disabled">un</Typography></InputAdornment>,
                    }}
                    sx={{ width: 130 }}
                    type="number"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
          Salvo localmente no navegador · futuro: sincronizado com o servidor
        </Typography>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Salvar configurações
        </Button>
      </DialogActions>
    </Dialog>
  )
}
